import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDoctorList, DoctorListItem } from '../hooks/useDoctorList';
import EmptyState from '../../../components/ui/EmptyState';
import ConfirmModal from '../../../components/common/ConfirmModal';
import SkeletonList from '../../../components/ui/SkeletonList';

const DoctorListPage: React.FC = () => {
  const {
    doctors,
    loading,
    error,
    pagination,
    pendingDeleteId,
    fetchDoctors,
    searchDoctors,
    deleteDoctor,
    cancelDelete,
    confirmDelete,
    goToPage,
  } = useDoctorList();

  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    searchDoctors(search);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="badge bg-success">Activo</span>
    ) : (
      <span className="badge bg-secondary">Inactivo</span>
    );
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Lista de Médicos</h3>
              <div className="card-tools">
                <Link to="/doctors/new" className="btn btn-primary btn-sm">
                  <i className="fa fa-plus"></i> Nuevo Médico
                </Link>
              </div>
            </div>

            <div className="card-body">
              {/* Search Form */}
              <div className="row" style={{ marginBottom: '20px' }}>
                <div className="col-md-6">
                  <form onSubmit={handleSearch} className="form-inline">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar por nombre o especialidad..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <span className="input-group-btn">
                        <button type="submit" className="btn btn-primary">
                          <i className="fa fa-search"></i>
                        </button>
                      </span>
                    </div>
                  </form>
                </div>
                <div className="col-md-6 text-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setSearch('');
                      fetchDoctors(1, '');
                    }}
                  >
                    <i className="fa fa-refresh"></i> Limpiar
                  </button>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger alert-dismissible">
                  <button type="button" className="close" data-dismiss="alert">
                    &times;
                  </button>
                  {error}
                </div>
              )}

              {/* Table */}
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Nombre</th>
                      <th>Especialidad</th>
                      <th>Teléfono</th>
                      <th>Celular</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                   <tbody>
                     {loading ? (
                       <tr>
                         <td colSpan={7} className="p-0">
                           <SkeletonList rowCount={5} columnCount={7} className="py-3" />
                         </td>
                       </tr>
                     ) : doctors.length === 0 ? (
                       <EmptyState variant="compact" icon="fa-user-md" title="No hay médicos registrados" action={{ label: 'Registrar médico', to: '/doctors/new', icon: 'fa-plus' }} />
                     ) : (
                      doctors.map((doctor: DoctorListItem) => (
                        <tr key={doctor.id}>
                          <td>{doctor.alias || `MED-${doctor.id}`}</td>
                          <td>
                            {doctor.first_name} {doctor.last_name}
                          </td>
                          <td>{doctor.specialty?.name || '-'}</td>
                          <td>{doctor.phone || '-'}</td>
                          <td>{doctor.mobile || '-'}</td>
                          <td>{getStatusBadge(doctor.is_active)}</td>
                          <td>
                            <div className="btn-group" role="group" aria-label={`Acciones del médico ${doctor.first_name} ${doctor.last_name}`}>
                              <Link
                                to={`/doctors/${doctor.id}`}
                                className="action-btn action-btn-view"
                                title="Ver"
                                aria-label={`Ver médico ${doctor.first_name} ${doctor.last_name}`}
                              >
                                <i className="fa fa-eye" aria-hidden="true"></i>
                              </Link>
                              <Link
                                to={`/doctors/${doctor.id}/edit`}
                                className="btn btn-primary btn-xs"
                                title="Editar"
                                aria-label={`Editar médico ${doctor.first_name} ${doctor.last_name}`}
                              >
                                <i className="fa fa-edit" aria-hidden="true"></i>
                              </Link>
                              {/* The endpoint retires the doctor, it never erases
                                  one: a consultation has to keep its author, so
                                  DELETE /doctors/{id} sets is_active = false. The
                                  label and the trash icon promised otherwise. */}
                              <button
                                className="btn btn-warning btn-xs"
                                title="Desactivar"
                                aria-label={`Desactivar médico ${doctor.first_name} ${doctor.last_name}`}
                                onClick={() => deleteDoctor(doctor.id)}
                              >
                                <i className="fa fa-user-slash" aria-hidden="true"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="dataTables_info">
                      Mostrando {pagination.currentPage} de {pagination.totalPages} páginas
                      (Total: {pagination.total} registros)
                    </div>
                  </div>
                  <div className="col-md-6">
                    <ul className="pagination pagination-sm float-end">
                      <li className={pagination.currentPage === 1 ? 'disabled' : ''}>
                        <button
                          className="btn btn-outline-secondary btn-xs"
                          onClick={() => goToPage(1)}
                          disabled={pagination.currentPage === 1}
                        >
                          &laquo;
                        </button>
                      </li>
                      {(() => {
                        const current = pagination.currentPage;
                        const total = pagination.totalPages;
                        const delta = 2;
                        const range: (number | '...')[] = [];
                        for (
                          let i = Math.max(2, current - delta);
                          i <= Math.min(total - 1, current + delta);
                          i++
                        ) {
                          range.push(i);
                        }
                        if (current - delta > 2) range.unshift('...');
                        if (current + delta < total - 1) range.push('...');
                        range.unshift(1);
                        if (total > 1) range.push(total);

                        return range.map((page, idx) =>
                          page === '...' ? (
                            <li key={`ellipsis-${idx}`} className="disabled">
                              <span className="btn btn-outline-secondary btn-xs">...</span>
                            </li>
                          ) : (
                            <li key={page} className={current === page ? 'active' : ''}>
                              <button
                                className="btn btn-outline-secondary btn-xs"
                                onClick={() => goToPage(page as number)}
                              >
                                {page}
                              </button>
                            </li>
                          )
                        );
                      })()}
                      <li className={pagination.currentPage === pagination.totalPages ? 'disabled' : ''}>
                        <button
                          className="btn btn-outline-secondary btn-xs"
                          onClick={() => goToPage(pagination.totalPages)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={pendingDeleteId !== null}
        title="Desactivar médico"
        body="El médico dejará de aparecer como disponible y no podrá recibir citas. Su historial de consultas se conserva y puede volver a activarlo cuando quiera."
        confirmLabel="Desactivar"
        confirmVariant="warning"
        loading={loading}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default DoctorListPage;
